import { isWithinCharacterCountLimit } from '@regional/shared/validation';

/**
 * Field length limits follow the Auth0 user profile attributes the account
 * application writes to:
 * https://auth0.com/docs/manage-users/user-accounts/user-profiles/user-profile-structure
 */

/** Display name, stored in the Auth0 `name` attribute: at most 300 characters (verified against the tenant) */
export const DISPLAY_NAME_MAX_LENGTH = 300;

/** Auth0 `email` attribute: the part before the `@` is at most 64 characters */
export const EMAIL_LOCAL_PART_MAX_LENGTH = 64;

/** Auth0 `email` attribute: the whole address (local part, `@`, domain) is at most 254 characters */
export const EMAIL_MAX_LENGTH = 254;

export function mailValidate(target: string): boolean {
  const pattern =
    /^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/;
  return pattern.test(target);
}

/**
 * Returns true if the email address length is within the Auth0 limits
 * (local part at most 64 characters, whole address at most 254 characters).
 */
export function emailLengthValidate(target: string): boolean {
  const localPart = target.split('@')[0];
  return (
    isWithinCharacterCountLimit(target, EMAIL_MAX_LENGTH) &&
    isWithinCharacterCountLimit(localPart, EMAIL_LOCAL_PART_MAX_LENGTH)
  );
}

export function phoneNumberValidate(target: string): boolean {
  // Regional (ph/vn) formats: digits with optional "+<country code>",
  // hyphens, spaces, and parentheses (e.g. "+63 (2) 8888-8888", "028 3822 9999")
  const allowedCharacters = /^\+?[0-9\-() ]+$/;
  const hasEnoughDigits = /[0-9]{3,}/;
  return allowedCharacters.test(target) && hasEnoughDigits.test(target);
}

/** バリデーターを受け取ってaddTagでバリデートを行うメソッド */
export const testValidate = (
  text: string,
  validator: ((t: string) => boolean) | undefined,
): boolean => (validator ? validator(text) : true);

/**
 * Returns true if the text is within the limit, counted in characters rather than bytes.
 * Auth0 defines its limits in characters, so accented letters such as "ñ" count as 1.
 */
export const lengthValidate = (text: string, limit: number): boolean =>
  isWithinCharacterCountLimit(text, limit);

/**
 * バックエンドから返されたURLの安全性を検証 (httpsかつ信頼できるドメインのみ許可)
 *
 * @param urlStr - 検証対象のURL
 * @param allowedHost - `*.auth0.com` 以外に許可するホスト名 (例: カスタムAuth0ドメイン)
 */
export const isSafeUrl = (urlStr: string, allowedHost?: string): boolean => {
  try {
    const parsed = new URL(urlStr);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname.endsWith('.auth0.com') ||
        (allowedHost !== undefined && parsed.hostname === allowedHost))
    );
  } catch {
    return false;
  }
};
